/**
 * fitting-quiz-data.js
 * SurfaceMaxx Pressure Washer Fitting Finder — Data Layer
 *
 * Contains three exports used by fitting-quiz.js:
 *   FQ_FITTINGS   — canonical fitting dictionary
 *   FQ_PRODUCTS   — normalized product catalog (sourced from CSV data)
 *   FQ_RULES      — compatibility rules engine
 *
 * MAINTENANCE: See bottom of file for notes on adding new products and rules.
 */

// ---------------------------------------------------------------------------
// 1. FITTINGS DICTIONARY
// ---------------------------------------------------------------------------
// Each entry is a canonical fitting type used as IDs throughout the rules engine.
// To add a new fitting type: copy any block, give it a unique id, fill all fields.
// Images: upload to Shopify Files (/admin/content/files) and paste URL in image field.
// ---------------------------------------------------------------------------

window.FQ_FITTINGS = {

  m22_14_male: {
    id: "m22_14_male",
    label: "M22-14mm Male",
    simple_label: "M22 Threaded — Male (outie)",
    family: "M22",
    type: "threaded",
    size: "14mm",
    gender: "male",
    context_hint: "Found on most gas pressure washer pump outlets, and on many hose ends",
    common_on: ["pressure_washer", "high_pressure_hose"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-m22-14-male.png?v=1776797162",
    badge: null,
    notes: [
      "M22 seals with an internal O-ring — do NOT use thread tape",
      "14mm bore is most common on gas washers and aftermarket hoses",
      "The outer ring is 22mm; the inner bore is what differs between 14 and 15mm"
    ]
  },

  m22_14_female: {
    id: "m22_14_female",
    label: "M22-14mm Female",
    simple_label: "M22 Threaded — Female (innie)",
    family: "M22",
    type: "threaded",
    size: "14mm",
    gender: "female",
    context_hint: "Found on most high-pressure hose ends and many spray gun inlets",
    common_on: ["high_pressure_hose", "spray_gun"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-m22-14-female.png?v=1776797161",
    badge: null,
    notes: [
      "M22 seals with an internal O-ring — do NOT use thread tape",
      "Standard hose-end fitting on most aftermarket and OEM hoses"
    ]
  },

  m22_15_male: {
    id: "m22_15_male",
    label: "M22-15mm Male",
    simple_label: "M22 Threaded — Male (outie, 15mm)",
    family: "M22",
    type: "threaded",
    size: "15mm",
    gender: "male",
    context_hint: "Found on Sun Joe, AR Blue Clean, Portland, and other budget electric washers",
    common_on: ["pressure_washer"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-m22-15-male.png?v=1776797162",
    badge: { text: "Looks identical to 14mm — measure bore to confirm", type: "warning" },
    notes: [
      "M22-15mm outer thread looks nearly identical to M22-14mm",
      "The 1mm bore difference means 14mm and 15mm will NOT seal correctly together",
      "Common on: Sun Joe SPX series, AR Blue Clean, Portland 1750/1850 PSI"
    ]
  },

  m22_15_female: {
    id: "m22_15_female",
    label: "M22-15mm Female",
    simple_label: "M22 Threaded — Female (innie, 15mm)",
    family: "M22",
    type: "threaded",
    size: "15mm",
    gender: "female",
    context_hint: "Found on hoses and guns paired with Sun Joe / AR Blue style washers",
    common_on: ["high_pressure_hose", "spray_gun"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-m22-15-female.png?v=1776797161",
    badge: { text: "Looks identical to 14mm — measure bore to confirm", type: "warning" },
    notes: [
      "Will thread onto M22-14mm male but WILL LEAK — not interchangeable",
      "Often found on OEM hoses that ship with Sun Joe / AR Blue washers"
    ]
  },

  qc_3_8_plug: {
    id: "qc_3_8_plug",
    label: '3/8" Quick Connect Plug',
    simple_label: '3/8" QC Plug — the "male" snap side',
    family: "QC",
    type: "quick_connect",
    size: "3/8in",
    gender: "plug",
    context_hint: 'Standard pre-gun quick connect. Found on spray guns and pump side of quick-connect setups',
    common_on: ["spray_gun", "pressure_washer"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-qc-38-plug.png?v=1776797161",
    badge: { text: "Pre-gun standard size", type: "info" },
    notes: [
      "The plug is the piece that gets pushed INTO the coupler",
      "3/8\" is the standard pre-gun (hose-to-gun) quick connect size",
      "Do NOT confuse with 1/4\" QC (post-gun) or air-tool quick connects"
    ]
  },

  qc_3_8_coupler: {
    id: "qc_3_8_coupler",
    label: '3/8" Quick Connect Coupler',
    simple_label: '3/8" QC Coupler — the "female" snap side',
    family: "QC",
    type: "quick_connect",
    size: "3/8in",
    gender: "coupler",
    context_hint: 'Standard pre-gun quick connect. Found on high-pressure hose ends in upgraded setups',
    common_on: ["high_pressure_hose"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-qc-38-coupler.png?v=1776797162",
    badge: { text: "Pre-gun standard size", type: "info" },
    notes: [
      "The coupler is the spring-loaded sleeve that the plug snaps INTO",
      "Pair with a 3/8\" QC plug — do not mix with 1/4\" QC"
    ]
  },

  qc_1_4_plug: {
    id: "qc_1_4_plug",
    label: '1/4" Quick Connect Plug',
    simple_label: '1/4" QC Plug — the "male" snap side',
    family: "QC",
    type: "quick_connect",
    size: "1/4in",
    gender: "plug",
    context_hint: 'Standard post-gun quick connect. Found on wands, nozzles, foam cannons, and accessories',
    common_on: ["wand", "nozzle", "foam_cannon", "surface_cleaner", "attachment"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-qc-14-plug.png?v=1776797160",
    badge: { text: "Post-gun standard size", type: "info" },
    notes: [
      "1/4\" QC is the standard post-gun (gun-to-wand / wand-to-accessory) size",
      "Smaller than the 3/8\" pre-gun size — they are NOT interchangeable"
    ]
  },

  qc_1_4_coupler: {
    id: "qc_1_4_coupler",
    label: '1/4" Quick Connect Coupler',
    simple_label: '1/4" QC Coupler — the "female" snap side',
    family: "QC",
    type: "quick_connect",
    size: "1/4in",
    gender: "coupler",
    context_hint: 'Standard post-gun coupler. Found on spray gun outlets and wand inlets',
    common_on: ["spray_gun", "wand"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-qc-14-coupler.png?v=1776797161",
    badge: { text: "Post-gun standard size", type: "info" },
    notes: [
      "Standard outlet on most modern spray guns",
      "Accepts 1/4\" QC plugs only — not 3/8\" QC plugs"
    ]
  },

  npt_3_8_male: {
    id: "npt_3_8_male",
    label: '3/8" NPT Male',
    simple_label: '3/8" Pipe Thread — Male (outie)',
    family: "NPT",
    type: "threaded",
    size: "3/8in",
    gender: "male",
    context_hint: "Found on some aftermarket hose ends and commercial-grade equipment",
    common_on: ["high_pressure_hose", "pressure_washer"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-npt-38-male.png?v=1776797162",
    badge: { text: "Requires thread sealant / Teflon tape", type: "warning" },
    notes: [
      "NPT (National Pipe Taper) is a tapered thread — use Teflon tape or thread sealant",
      "Do NOT confuse with GHT garden hose thread — different pitch and taper"
    ]
  },

  npt_3_8_female: {
    id: "npt_3_8_female",
    label: '3/8" NPT Female',
    simple_label: '3/8" Pipe Thread — Female (innie)',
    family: "NPT",
    type: "threaded",
    size: "3/8in",
    gender: "female",
    context_hint: "Found on some pump outlets, commercial guns, and inline components",
    common_on: ["pressure_washer", "spray_gun"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-npt-38-female.png?v=1776797161",
    badge: { text: "Requires thread sealant / Teflon tape", type: "warning" },
    notes: [
      "Use Teflon tape or pipe thread sealant — NPT relies on thread sealing not O-rings"
    ]
  },

  npt_1_4_male: {
    id: "npt_1_4_male",
    label: '1/4" NPT Male',
    simple_label: '1/4" Pipe Thread — Male (outie)',
    family: "NPT",
    type: "threaded",
    size: "1/4in",
    gender: "male",
    context_hint: "Found on some wands, nozzle bodies, and commercial accessories",
    common_on: ["wand", "attachment"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-npt-14-male.png?v=1776797161",
    badge: { text: "Requires thread sealant / Teflon tape", type: "warning" },
    notes: [
      "Use Teflon tape or pipe thread sealant",
      "Smaller than 3/8\" NPT — not interchangeable without a reducer"
    ]
  },

  npt_1_4_female: {
    id: "npt_1_4_female",
    label: '1/4" NPT Female',
    simple_label: '1/4" Pipe Thread — Female (innie)',
    family: "NPT",
    type: "threaded",
    size: "1/4in",
    gender: "female",
    context_hint: "Found on some gun outlets, wands, and inline components",
    common_on: ["spray_gun", "wand"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-npt-14-female.png?v=1776797161",
    badge: { text: "Requires thread sealant / Teflon tape", type: "warning" },
    notes: [
      "Use Teflon tape or pipe thread sealant"
    ]
  },

  ght_3_4_male: {
    id: "ght_3_4_male",
    label: '3/4" Garden Hose Thread — Male',
    simple_label: "Garden hose fitting — Male (outie)",
    family: "GHT",
    type: "threaded",
    size: "3/4in",
    gender: "male",
    context_hint: "This is a standard garden hose fitting — LOW PRESSURE ONLY",
    common_on: ["garden_hose"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-ght-male.png?v=1776797161",
    badge: { text: "LOW PRESSURE ONLY — not for high-pressure side", type: "danger" },
    notes: [
      "GHT is garden hose thread — only for the water SUPPLY inlet on the pressure washer",
      "NEVER connect garden hose fittings to the high-pressure outlet / hose / gun side",
      "GHT thread pitch differs from NPT — do not force NPT into GHT"
    ]
  },

  ght_3_4_female: {
    id: "ght_3_4_female",
    label: '3/4" Garden Hose Thread — Female',
    simple_label: "Garden hose fitting — Female (innie)",
    family: "GHT",
    type: "threaded",
    size: "3/4in",
    gender: "female",
    context_hint: "This is a standard garden hose fitting — LOW PRESSURE ONLY",
    common_on: ["garden_hose", "pressure_washer"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-ght-female.png?v=1776797161",
    badge: { text: "LOW PRESSURE ONLY — water inlet only", type: "danger" },
    notes: [
      "GHT female is the water supply inlet on most pressure washers",
      "Do NOT use on the high-pressure outlet side"
    ]
  },

  proprietary: {
    id: "proprietary",
    label: "Proprietary / Brand-Specific",
    simple_label: "Brand-specific fitting (Kärcher, Bosch, Sun Joe, etc.)",
    family: "proprietary",
    type: "proprietary",
    size: null,
    gender: null,
    context_hint: "Used on Kärcher K-series, Bosch AQT, Nilfisk, Lavor, and some Sun Joe systems",
    common_on: ["pressure_washer", "spray_gun", "wand"],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-proprietary.png",
    badge: { text: "Brand-specific — not universally compatible", type: "warning" },
    notes: [
      "Kärcher K-series uses a proprietary quick-connect bayonet between hose and gun",
      "Bosch AQT, Nilfisk, and Lavor have similar but non-interchangeable bayonet systems",
      "Some Sun Joe models use proprietary connections at gun/wand",
      "Usually requires a brand-specific adapter to connect to universal accessories"
    ]
  },

  unknown: {
    id: "unknown",
    label: "I'm Not Sure",
    simple_label: "I don't know / can't tell",
    family: null,
    type: null,
    size: null,
    gender: null,
    context_hint: "That's okay — we'll show you the most likely options",
    common_on: [],
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fitting-unknown.png",
    badge: { text: "We'll help you figure it out", type: "info" },
    notes: []
  }
};


// ---------------------------------------------------------------------------
// 2. PRODUCT CATALOG
// ---------------------------------------------------------------------------
// Sourced directly from SurfaceMaxx CSV exports (Adapters, Plugs, Couplers,
// Quick Connect Sets). end_a / end_b map to FQ_FITTINGS keys above.
//
// NOTE: All image URLs are live CDN URLs from the CSV data.
// NOTE: All product URLs are live SurfaceMaxx storefront URLs from the CSV data.
// VERIFY: Products marked // VERIFY need human confirmation of fitting ends.
// ---------------------------------------------------------------------------

window.FQ_PRODUCTS = [

  // --- ADAPTERS (from Adapters CSV) ---

  {
    sku: "SGY-PWA23",
    variant_id: '49116670656800',
    title: "SurfaceMaxx 3/8-in Male Pipe Thread x Male M-22",
    handle: "surfacemaxx-3-8-in-male-pipe-thread-x-male-m22",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-male-pipe-thread-x-male-m22",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-male-pipe-thread-x-male-m-22-367230.jpg?v=1741918200",
    category: "adapter",
    end_a: "m22_14_male",
    end_b: "npt_3_8_male",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 2,
    description: "Adapts an M22-14mm male outlet to a 3/8-in male NPT connection. Use with thread sealant on the NPT end."
  },

  {
    sku: "SGY-PWA24",
    variant_id: '49116677964064',
    title: "SurfaceMaxx 3/8-in Female Pipe Thread x Female M-22",
    handle: "surfacemaxx-3-8-in-female-pipe-thread-x-female-m22",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-female-pipe-thread-x-female-m22",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-female-pipe-thread-x-female-m-22-461773.jpg?v=1741918355",
    category: "adapter",
    end_a: "m22_14_female",
    end_b: "npt_3_8_female",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 2,
    description: "Adapts an M22-14mm female port to a 3/8-in female NPT connection. Both ends are female."
  },

  {
    sku: "SGY-PWA93",
    title: "SurfaceMaxx M22 15mm to M22 14mm Reducer Adapter (Sun Joe)",
    handle: "15mm-to-14mm-reducer-adapter-for-any-sunjoe",
    url: "https://surfacemaxx.com/products/15mm-to-14mm-reducer-adapter-for-any-sunjoe",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/15mm-to-14mm-reducer-adapter-for-any-sunjoesku-219381.jpg?v=1738012183",
    category: "adapter",
    end_a: "m22_15_female",  // threads onto M22-15mm male pump outlet
    end_b: "m22_14_male",    // exposes M22-14mm male for standard adapters
    installs_on: "pump",
    contexts: ["pump_to_hose"],
    priority: 1,
    description: "Converts a Sun Joe / AR Blue Clean / Portland M22-15mm male pump outlet to M22-14mm male. Once installed, use any standard M22-14mm adapter or hose. Required first step when adding quick-connect or extending hoses on 15mm pump systems."
  },

  {
    sku: "SGY-PWA7",
    variant_id: '49116658172192',
    title: "SurfaceMaxx M-22 Hose to Hose Connector",
    handle: "surfacemaxx-m-22-hose-to-hose-connector",
    url: "https://surfacemaxx.com/products/surfacemaxx-m-22-hose-to-hose-connector",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m-22-hose-to-hose-connector-428103.jpg?v=1741918355",
    category: "adapter",
    end_a: "m22_14_male",
    end_b: "m22_14_male",
    installs_on: "hose",
    contexts: ["hose_to_hose"],
    priority: 1,
    description: "Male M22-14mm on both ends. Joins two M22 female hose ends together for hose extension."
  },

  {
    sku: "SMX-PWA303",
    variant_id: '49136151593248',
    title: "SurfaceMaxx M22 Male x 1/4-in Male NPT",
    handle: "surfacemaxx-m22-male-x-1-4-in-male-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-male-x-1-4-in-male-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/12066863.jpg?v=1743100186",
    category: "adapter",
    end_a: "m22_14_male",
    end_b: "npt_1_4_male",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun", "gun_to_wand"],
    priority: 2,
    description: "M22-14mm male on one end, 1/4-in male NPT on the other. Bridges M22 to 1/4-in NPT threaded connections."
  },

  {
    sku: "SMX-PWA309",
    variant_id: '49136135799072',
    title: "SurfaceMaxx M22 Female x 1/4-in Female NPT",
    handle: "surfacemaxx-m22-female-x-1-4-inch-female-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-female-x-1-4-inch-female-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-female-x-14-in-female-npt-640339.jpg?v=1743089405",
    category: "adapter",
    end_a: "m22_14_female",
    end_b: "npt_1_4_female",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 2,
    description: "M22-14mm female on one end, 1/4-in female NPT on the other. Both ends are female."
  },

  {
    sku: "SMX-PWA310",
    variant_id: '49454378647840',
    title: "SurfaceMaxx M22 Female x 1/4-in Male NPT",
    handle: "surfacemaxx-m22-female-x-1-4-in-male-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-female-x-1-4-in-male-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-female-x-14-in-male-npt-205006.jpg?v=1743089296",
    category: "adapter",
    end_a: "m22_14_female",
    end_b: "npt_1_4_male",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun", "gun_to_wand"],
    priority: 1,
    description: "M22-14mm female on one end, 1/4-in male NPT on the other. Useful for connecting M22 hose ends to 1/4-in NPT ports."
  },

  {
    sku: "SMX-PWA311",
    variant_id: '49454384185632',
    title: "SurfaceMaxx M22 Female x 3/8-in Male NPT",
    handle: "surfacemaxx-m22-female-x-3-8-in-male-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-female-x-3-8-in-male-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-female-x-38-in-male-npt-350928.jpg?v=1743089296",
    category: "adapter",
    end_a: "m22_14_female",
    end_b: "npt_3_8_male",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "M22-14mm female on one end, 3/8-in male NPT on the other. Bridges M22 hose ends to 3/8-in NPT inlets."
  },

  {
    sku: "SMX-PWA312",
    variant_id: '49454388773152',
    title: "SurfaceMaxx M22 Male x 1/4-in Female NPT",
    handle: "surfacemaxx-m22-male-x-1-4-in-female-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-male-x-1-4-in-female-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/12066892.jpg?v=1742936898",
    category: "adapter",
    end_a: "m22_14_male",
    end_b: "npt_1_4_female",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun", "gun_to_wand"],
    priority: 1,
    description: "M22/14mm male on one end, 1/4-in female NPT on the other. Note: M22 bore is 14mm (not 15mm) — standard for most gas pressure washers and aftermarket hoses."
  },

  {
    sku: "SMX-PWA313",
    variant_id: '49136120267040',
    title: "SurfaceMaxx M22 Male x 3/8-in Female NPT",
    handle: "surfacemaxx-m22-male-threaded-x-3-8-inch-female-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-male-threaded-x-3-8-inch-female-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-male-x-38-in-female-npt-508412.jpg?v=1743007587",
    category: "adapter",
    end_a: "m22_14_male",
    end_b: "npt_3_8_female",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "M22-14mm male on one end, 3/8-in female NPT on the other."
  },

  {
    sku: "SMX-PWA314",
    variant_id: '49136110305568',
    title: "SurfaceMaxx M22 Male x 1/4-in Female NPT",
    handle: "surfacemaxx-m22-male-threaded-x-1-4-inch-female-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-male-threaded-x-1-4-inch-female-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-male-x-14-in-female-npt-501005.jpg?v=1743007589",
    category: "adapter",
    end_a: "m22_14_male",
    end_b: "npt_1_4_female",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun", "gun_to_wand"],
    priority: 2,
    description: "M22/14mm male on one end, 1/4-in female NPT on the other. Note: M22 bore is 14mm (not 15mm). Distinct from SMX-PWA312 — different product page, image, and SKU confirmed in catalog."
  },

  // --- PLUGS (from Plugs CSV) ---

  {
    sku: "SMX-PWA301",
    variant_id: '49431669473568',
    title: "SurfaceMaxx 3/8-in Female NPT x 3/8-in Quick Connect Plug",
    handle: "surfacemaxx-3-8-inch-female-npt-x-3-8-inch-quick-connect-plug",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-inch-female-npt-x-3-8-inch-quick-connect-plug",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/12066859.jpg?v=1743106352",
    category: "plug",
    end_a: "npt_3_8_female",
    end_b: "qc_3_8_plug",
    installs_on: "hose_or_pump",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "3/8-in female NPT threads onto a 3/8-in NPT male port; the other end is a 3/8-in QC plug that snaps into any 3/8-in QC coupler. Use thread sealant on NPT end."
  },

  {
    sku: "SGY-PWA26",
    variant_id: '49095639925024',
    title: "SurfaceMaxx 3/8-in Quick Disconnect Plug x Male M-22",
    handle: "surfacemaxx-3-8-in-quick-disconnect-plug-x-male-m-22",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-quick-disconnect-plug-x-male-m-22",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-quick-disconnect-plug-x-male-m-22-565646.jpg?v=1741918450",
    category: "plug",
    end_a: "m22_14_male",
    end_b: "qc_3_8_plug",
    installs_on: "pump",
    contexts: ["pump_to_hose"],
    priority: 1,
    description: "Threads onto an M22-14mm male pump outlet. Other end is a 3/8-in QC plug. Converts your pump to quick-connect so you can snap on any QC-equipped hose."
  },

  {
    sku: "SGY-PWA27",
    variant_id: '49116644016416',
    title: "SurfaceMaxx M22 Female x 3/8-in Quick Connect Plug",
    handle: "surfacemaxx-m22-female-x-3-8-in-plug",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-female-x-3-8-in-plug",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-female-x-38-in-plug-174193.jpg?v=1741918358",
    category: "plug",
    end_a: "m22_14_female",
    end_b: "qc_3_8_plug",
    installs_on: "hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "M22-14mm female end threads onto an M22 male port (pump outlet or hose). Other end is a 3/8-in QC plug for snapping into any 3/8-in QC coupler."
  },

  {
    sku: "SGY-PWA22",
    variant_id: '49116647260448',
    title: "SurfaceMaxx 3/8-in Quick Disconnect Plug x 3/8-in NPT Male",
    handle: "surfacemaxx-3-8-in-quick-disconnect-plug-x-3-8-in",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-quick-disconnect-plug-x-3-8-in",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-quick-disconnect-plug-x-38-in-118863.jpg?v=1741918358",
    category: "plug",
    end_a: "npt_3_8_male",
    end_b: "qc_3_8_plug",
    installs_on: "pump_or_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "3/8-in male NPT threads into a female NPT port. Other end is a 3/8-in QC plug. Use thread sealant on NPT end."
  },

  {
    sku: "SMX-PWA304",
    variant_id: '49136149463328',
    title: "SurfaceMaxx 1/4-in Female NPT x 1/4-in Quick Connect Plug",
    handle: "surfacemaxx-1-4-inch-female-npt-x-1-4-inch-quick-connect-plug",
    url: "https://surfacemaxx.com/products/surfacemaxx-1-4-inch-female-npt-x-1-4-inch-quick-connect-plug",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/12066866.jpg?v=1743098267",
    category: "plug",
    end_a: "npt_1_4_female",
    end_b: "qc_1_4_plug",
    installs_on: "wand_or_attachment",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "1/4-in female NPT end threads onto a 1/4-in male NPT port (wand or accessory). Other end is a 1/4-in QC plug."
  },

  {
    sku: "SMX-PWA317",
    variant_id: '49136103686432',
    title: "SurfaceMaxx 1/4-in Male NPT x 1/4-in Quick Connect Plug",
    handle: "surfacemaxx-1-4-inch-male-npt-x-1-4-inch-quick-connect-plug",
    url: "https://surfacemaxx.com/products/surfacemaxx-1-4-inch-male-npt-x-1-4-inch-quick-connect-plug",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-14-in-male-npt-x-14-in-plug-191453.jpg?v=1742917715",
    category: "plug",
    end_a: "npt_1_4_male",
    end_b: "qc_1_4_plug",
    installs_on: "wand_or_attachment",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "1/4-in male NPT threads into a female NPT port. Other end is a 1/4-in QC plug for post-gun accessories."
  },

  {
    sku: "SMX-PWA315",
    variant_id: '49136122495264',
    title: "SurfaceMaxx M22 Female x 1/4-in Quick Connect Plug",
    handle: "surfacemaxx-m22-female-threaded-x-1-4-inch-quick-connect-plug",
    url: "https://surfacemaxx.com/products/surfacemaxx-m22-female-threaded-x-1-4-inch-quick-connect-plug",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-female-x-14-in-plug-101669.jpg?v=1743007588",
    category: "plug",
    end_a: "m22_14_female",
    end_b: "qc_1_4_plug",
    installs_on: "wand_or_attachment",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "M22-14mm female end threads onto an M22 male port (wand or attachment). Other end is a 1/4-in QC plug for post-gun accessories."
  },

  // --- COUPLERS (from Couplers CSV) ---

  {
    sku: "SGY-PWA25",
    variant_id: '49116638708000',
    title: "SurfaceMaxx 3/8-in Quick Disconnect Coupler x Male M-22",
    handle: "surfacemaxx-3-8-in-quick-disconnect-coupler-x-male-m-22",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-quick-disconnect-coupler-x-male-m-22",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-quick-disconnect-coupler-x-male-m-22-884516.jpg?v=1741918360",
    category: "coupler",
    end_a: "m22_14_male",
    end_b: "qc_3_8_coupler",
    installs_on: "hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "M22-14mm male end threads into an M22 female port on a hose. Other end is a 3/8-in QC coupler that accepts any 3/8-in QC plug."
  },

  {
    sku: "SGY-PWA30",
    variant_id: '49116612460832',
    title: "SurfaceMaxx 3/8-in Quick Disconnect Coupler x Female M-22",
    handle: "surfacemaxx-3-8-in-quick-disconnect-coupler-x-female-m-22",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-quick-disconnect-coupler-x-female-m-22",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-quick-disconnect-coupler-x-female-m-22-182100.jpg?v=1741918360",
    category: "coupler",
    end_a: "m22_14_female",
    end_b: "qc_3_8_coupler",
    installs_on: "hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "M22-14mm female end threads onto an M22 male port (pump or hose). Other end is a 3/8-in QC coupler that accepts any 3/8-in QC plug."
  },

  {
    sku: "SMX-PWA300",
    variant_id: '49136199139616',
    title: "SurfaceMaxx 1/4-in Female Coupler x 1/4-in Female NPT",
    handle: "surfacemaxx-1-4-inch-female-quick-connect-coupler-x-1-4-inch-female-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-1-4-inch-female-quick-connect-coupler-x-1-4-inch-female-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-14-in-female-coupler-x-14-in-female-npt-595627.jpg?v=1743172121",
    category: "coupler",
    end_a: "npt_1_4_female",
    end_b: "qc_1_4_coupler",
    installs_on: "gun_or_wand",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "1/4-in female NPT end threads onto a 1/4-in male NPT outlet (gun or wand). Other end is a 1/4-in QC coupler."
  },

  {
    sku: "SMX-PWA302",
    variant_id: '49136153854240',
    title: "SurfaceMaxx 3/8-in Male NPT x 3/8-in Female Quick Connect Coupler",
    handle: "surfacemaxx-3-8-inch-female-quick-connect-coupler-x-3-8-inch-male-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-inch-female-quick-connect-coupler-x-3-8-inch-male-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/12066860.jpg?v=1743103850",
    category: "coupler",
    end_a: "npt_3_8_male",
    end_b: "qc_3_8_coupler",
    installs_on: "hose_or_pump",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "3/8-in male NPT threads into a female NPT port. Other end is a 3/8-in QC coupler. Use thread sealant on NPT end."
  },

  {
    sku: "SMX-PWA316",
    variant_id: '49136101556512',
    title: "SurfaceMaxx 1/4-in Male NPT x 1/4-in Female Quick Connect Coupler",
    handle: "surfacemaxx-1-4-inch-female-quick-connect-coupler-x-1-4-inch-male-npt",
    url: "https://surfacemaxx.com/products/surfacemaxx-1-4-inch-female-quick-connect-coupler-x-1-4-inch-male-npt",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-14-in-male-npt-x-14-in-female-coupler-247716.jpg?v=1742917715",
    category: "coupler",
    end_a: "npt_1_4_male",
    end_b: "qc_1_4_coupler",
    installs_on: "gun_or_wand",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "1/4-in male NPT threads into a female NPT port (gun or wand outlet). Other end is a 1/4-in QC coupler."
  },

  // --- QUICK CONNECT SETS (from Quick Connect Sets CSV) ---

  {
    sku: "SGY-PWA3",
    variant_id: '49116619374880',
    title: "SurfaceMaxx 1/4-in Quick Disconnect Set",
    handle: "surfacemaxx-1-4-in-quick-disconnect-set",
    url: "https://surfacemaxx.com/products/surfacemaxx-1-4-in-quick-disconnect-set",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-14-in-quick-disconnect-set-598833.jpg?v=1741918360",
    category: "quick_connect_set",
    end_a: "npt_1_4_female",   // coupler side
    end_b: "npt_1_4_male",     // plug side
    qc_size: "1/4in",
    installs_on: "gun_and_wand",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "Contains one 1/4-in QC coupler (female NPT) and one 1/4-in QC plug (male NPT). Converts a matched NPT gun outlet and wand inlet to quick-connect. Use thread sealant on both NPT ends."
  },

  {
    sku: "SGY-PWA10",
    variant_id: '49116600566048',
    title: "SurfaceMaxx M22 Quick Connect Set",
    handle: "m-22-quick-connect-set",
    url: "https://surfacemaxx.com/products/m-22-quick-connect-set",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m22-quick-connect-set-114037.jpg?v=1743089504",
    category: "quick_connect_set",
    end_a: "m22_14_male",    // coupler side (M22 male x 3/8 QC coupler)
    end_b: "m22_14_female",  // plug side (M22 female x 3/8 QC plug)
    qc_size: "3/8in",
    installs_on: "pump_and_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "Contains one 3/8-in QC coupler with M22-14mm male end and one 3/8-in QC plug with M22-14mm female end. Converts a standard M22 pump and hose to quick-connect. Best for gas washers with M22-14mm pump outlets."
  },

  {
    sku: "SGY-PWA6",
    variant_id: '49116660531488',
    title: "SurfaceMaxx 3/8-in Quick Disconnect Set",
    handle: "surfacemaxx-3-8-in-quick-disconnect-set",
    url: "https://surfacemaxx.com/products/surfacemaxx-3-8-in-quick-disconnect-set",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-38-in-quick-disconnect-set-244259.jpg?v=1741918356",
    category: "quick_connect_set",
    end_a: "npt_3_8_female",  // coupler side
    end_b: "npt_3_8_female",  // plug side — both ends are female 3/8" NPT; plug and coupler each thread onto male NPT ports
    qc_size: "3/8in",
    installs_on: "hose_and_pump",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "Contains one 3/8-in QC coupler and one 3/8-in QC plug, both with 3/8-in female NPT ends. Both the plug and coupler thread onto 3/8-in male NPT ports — this is correct. Converts 3/8-in NPT threaded equipment to quick-connect. Use thread sealant on both NPT ends."
  },

  {
    sku: "SGY-PWA98",
    variant_id: '49156015554848',
    title: "SurfaceMaxx M-22 to 1/4-in Quick Connect Kit",
    handle: "surfacemaxx-m-22-to-1-4-in-quick-connect-kit",
    url: "https://surfacemaxx.com/products/surfacemaxx-m-22-to-1-4-in-quick-connect-kit",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/surfacemaxx-m-22-to-14-in-quick-connect-kit-504684.jpg?v=1741918145",
    category: "quick_connect_set",
    end_a: "m22_14_male",    // coupler side
    end_b: "m22_14_female",  // plug side
    qc_size: "1/4in",
    installs_on: "pump_and_hose",
    contexts: ["gun_to_wand"],
    priority: 2,
    description: "Contains one 1/4-in QC coupler with M22-14mm male end and one 1/4-in QC plug with M22-14mm female end. Designed for gun-to-wand connections — not for pump or hose applications. Converts M22 gun outlet and wand inlet to 1/4-in quick-connect."
  },

  {
    sku: "SGY-PWA78",
    variant_id: '49095646806304',
    title: "SurfaceMaxx Quick Connect Kit 4500 PSI",
    handle: "surfacemaxx-quick-connect-kit-4500-psi",
    url: "https://surfacemaxx.com/products/surfacemaxx-quick-connect-kit-4500-psi",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/12098600.jpg?v=1743181870",
    category: "quick_connect_set",
    end_a: "m22_14_female",  // coupler side
    end_b: "m22_14_male",    // plug side
    qc_size: "3/8in",
    installs_on: "pump_and_hose",
    contexts: ["pump_to_hose", "hose_to_gun"],
    priority: 1,
    description: "Heavy-duty quick connect kit rated to 4500 PSI. Contains one 3/8-in QC coupler with M22-14mm female end and one 3/8-in QC plug with M22-14mm male end. Ideal for high-pressure applications and commercial-grade equipment."
  },

  // --- NEW ATTACHMENT PRODUCTS ---

  {
    sku: "SMX-EW-9-18",
    title: "SurfaceMaxx Extension Wand (9 or 18 ft)",
    handle: "surfacemaxx-extension-wand",
    url: "https://surfacemaxx.com/products/surfacemaxx-extension-wand",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fq-icon-extension-wand.png?v=1773436060",
    category: "extension_wand",
    end_a: "qc_1_4_plug",   // wand inlet connects to gun QC coupler
    end_b: "qc_1_4_coupler", // wand tip has QC coupler for nozzles/attachments
    installs_on: "gun",
    contexts: ["gun_to_wand", "wand_to_attachment"],
    priority: 1,
    description: "SurfaceMaxx telescoping extension wand. 9 or 18 ft reach. Inlet is a 1/4-in QC plug that snaps into any standard gun outlet. Tip is a 1/4-in QC coupler for nozzles and attachments."
  },

  {
    sku: "SMX-SC-WB",
    title: "SurfaceMaxx Walk-Behind Surface Cleaner",
    handle: "surfacemaxx-walk-behind-surface-cleaner",
    url: "https://surfacemaxx.com/products/surfacemaxx-walk-behind-surface-cleaner",
    image: "https://cdn.shopify.com/s/files/1/0895/7820/9568/files/fq-icon-walk-behind-surface-cleaner.png?v=1773436060",
    category: "walk_behind_surface_cleaner",
    end_a: "qc_1_4_plug",   // attachment inlet — standard 1/4-in QC plug
    end_b: null,
    installs_on: "wand_or_gun",
    contexts: ["wand_to_attachment", "gun_to_wand"],
    priority: 1,
    description: "Large walk-behind rotating disc cleaner for driveways, patios, and large flat surfaces. Connects via standard 1/4-in QC plug to any wand or gun outlet with a QC coupler."
  }

];


// ---------------------------------------------------------------------------
// 3. COMPATIBILITY RULES ENGINE
// ---------------------------------------------------------------------------
// Rules are matched by: context + have_fitting + want_fitting
// Priority order: exact match → family match → unknown branch
//
// result_type values:
//   direct_fit            — connect directly, no parts needed
//   adapter_needed        — one product bridges the connection
//   multiple_products     — two or more products needed
//   not_compatible        — physically will not work / unsafe
//   possible_match        — likely but uncertain (unknown fitting selected)
//   needs_support         — too ambiguous to automate; show support CTA
//
// recommended_skus: array of SKUs from FQ_PRODUCTS above
// ---------------------------------------------------------------------------

window.FQ_RULES = [

  // =========================================================================
  // CONTEXT: pump_to_hose
  // =========================================================================

  // Direct fits
  {
    id: "pump_m22_14m_hose_m22_14f",
    context: "pump_to_hose",
    have_fitting: "m22_14_male",
    want_fitting: "m22_14_female",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your pump's M22-14mm male outlet connects directly to an M22-14mm female hose. No adapter needed.",
    install_detail: "Thread the hose end onto the pump outlet by hand, then tighten a quarter turn with a wrench. M22 uses an internal O-ring — do NOT use Teflon tape.",
    warnings: ["Do not use Teflon tape on M22 — it seals with an O-ring", "If it leaks, replace the O-ring inside the fitting"],
    confidence: "high"
  },

  {
    id: "pump_m22_15m_hose_m22_15f",
    context: "pump_to_hose",
    have_fitting: "m22_15_male",
    want_fitting: "m22_15_female",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your M22-15mm pump outlet connects directly to an M22-15mm female hose. No adapter needed.",
    install_detail: "Thread the hose end onto the pump outlet. M22 uses an O-ring — do NOT use Teflon tape.",
    warnings: ["Common on Sun Joe, AR Blue Clean, and Portland washers", "Do not mix 14mm and 15mm — they look identical but will leak"],
    confidence: "high"
  },

  {
    id: "pump_qc38plug_hose_qc38coupler",
    context: "pump_to_hose",
    have_fitting: "qc_3_8_plug",
    want_fitting: "qc_3_8_coupler",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your pump already has a 3/8\" QC plug — just snap the hose coupler onto it.",
    install_detail: "Pull back the coupler sleeve, push it onto the plug, and release. It should click and lock.",
    warnings: ["If it leaks at the snap joint, inspect and replace the QC O-ring"],
    confidence: "high"
  },

  {
    id: "pump_npt38m_hose_npt38f",
    context: "pump_to_hose",
    have_fitting: "npt_3_8_male",
    want_fitting: "npt_3_8_female",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "3/8\" NPT male to 3/8\" NPT female — thread directly together.",
    install_detail: "Apply Teflon tape or pipe thread sealant to the male threads before connecting.",
    warnings: ["Always use thread sealant on NPT connections — they do not use O-rings"],
    confidence: "high"
  },

  // Adapter cases: M22 pump to QC hose
  {
    id: "pump_m22_14m_hose_qc38coupler",
    context: "pump_to_hose",
    have_fitting: "m22_14_male",
    want_fitting: "qc_3_8_coupler",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA30"],
    install_on: "pump",
    message: "You need one adapter to add quick-connect capability to your M22-14mm pump.",
    install_detail: "Thread SGY-PWA30 (M22 Female × 3/8\" QC Coupler) onto your pump's M22-14mm male outlet. Your hose's 3/8\" QC plug will then snap into the coupler.",
    warnings: ["Do not use Teflon tape on the M22 end — it seals with an O-ring"],
    confidence: "high"
  },

  {
    id: "pump_m22_15m_hose_qc38coupler",
    context: "pump_to_hose",
    have_fitting: "m22_15_male",
    want_fitting: "qc_3_8_coupler",
    result_type: "multiple_products",
    recommended_skus: ["SGY-PWA93", "SGY-PWA30"],
    install_on: "pump",
    message: "Your M22-15mm pump needs two adapters to connect to a quick-connect hose.",
    install_detail: "Step 1 — Thread SGY-PWA93 (15mm → 14mm Reducer) onto your pump's M22-15mm male outlet. This converts it to a standard M22-14mm male. Step 2 — Thread SGY-PWA30 (M22 Female × 3/8\" QC Coupler) onto SGY-PWA93's M22-14mm male end. Your hose's 3/8\" QC plug then snaps into the coupler.",
    warnings: ["M22-15mm is NOT the same as M22-14mm — do not skip the reducer step", "Common on: Sun Joe SPX, AR Blue Clean, Portland 1750/1850 PSI", "No Teflon tape on either M22 connection — both seal with O-rings"],
    confidence: "high"
  },

  {
    id: "pump_m22_14m_hose_m22_15f",
    context: "pump_to_hose",
    have_fitting: "m22_14_male",
    want_fitting: "m22_15_female",
    result_type: "possible_match",
    recommended_skus: [],
    install_on: "hose",
    message: "M22-14mm and M22-15mm don't connect directly — but a third-party conversion coupler can bridge the gap.",
    install_detail: "You need a M22-15mm Male to M22-14mm Female conversion coupler. We don't carry this as a standalone product, but compatible options are available from third-party sellers: (1) Walmart: 1-Piece M22-15mm Male to 14mm Female Pressure Washer Coupler (https://www.walmart.com/ip/19996166027) — (2) Amazon: Boltigen M22-15mm to M22-14mm Connector (https://www.amazon.com/dp/B0CRPBSX87). Thread the coupler onto your hose's M22-15mm female end to convert it to M22-14mm female, then connect to the pump's M22-14mm male outlet.",
    warnings: ["M22-14mm and M22-15mm look nearly identical — do not force a direct connection without the adapter", "This conversion coupler is not a SurfaceMaxx product — verify it is rated for your pressure washer's PSI"],
    confidence: "medium"
  },

  {
    id: "pump_m22_15m_hose_m22_14f",
    context: "pump_to_hose",
    have_fitting: "m22_15_male",
    want_fitting: "m22_14_female",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA93"],
    install_on: "pump",
    message: "Your M22-15mm pump and M22-14mm hose need a reducer adapter — but it's a quick fix.",
    install_detail: "Thread SGY-PWA93 (M22 15mm → 14mm Reducer) onto your pump's M22-15mm male outlet. Your M22-14mm female hose will then thread directly onto SGY-PWA93's M22-14mm male end. No Teflon tape on either M22 end — both seal with O-rings.",
    warnings: ["Do NOT thread a 14mm hose directly onto a 15mm pump — it will leak under pressure", "SGY-PWA93 is specifically designed for Sun Joe SPX, AR Blue Clean, and Portland washers"],
    confidence: "high"
  },

  {
    id: "pump_npt38m_hose_qc38coupler",
    context: "pump_to_hose",
    have_fitting: "npt_3_8_male",
    want_fitting: "qc_3_8_coupler",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA301"],
    install_on: "pump",
    message: "You need one adapter to add quick-connect capability to your 3/8\" NPT pump.",
    install_detail: "Thread SMX-PWA301 (3/8\" Female NPT × 3/8\" QC Plug) onto your pump's NPT male outlet using thread sealant. Your hose's 3/8\" QC coupler then snaps onto the plug.",
    warnings: ["Always use Teflon tape or thread sealant on NPT connections — they do not seal with O-rings"],
    confidence: "high"
  },

  {
    id: "pump_ght_any_hp",
    context: "pump_to_hose",
    have_fitting: "ght_3_4_male",
    want_fitting: null,
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "A garden hose fitting CANNOT be used on the high-pressure side.",
    install_detail: "Garden hose thread (GHT) is for the low-pressure water supply inlet only. It is not rated for pressure washer discharge pressure and will fail.",
    warnings: ["SAFETY: Do not attempt to connect garden hose fittings to the high-pressure outlet", "The water inlet (garden hose supply) and pressure outlet are completely separate connections"],
    confidence: "high"
  },

  {
    id: "pump_ght_any_hp2",
    context: "pump_to_hose",
    have_fitting: "ght_3_4_female",
    want_fitting: null,
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "A garden hose fitting CANNOT be used on the high-pressure side.",
    install_detail: "Garden hose thread is for the water supply inlet only — not the pressure outlet.",
    warnings: ["SAFETY: Do not use garden hose fittings on the high-pressure side"],
    confidence: "high"
  },

  {
    id: "pump_proprietary_any",
    context: "pump_to_hose",
    have_fitting: "proprietary",
    want_fitting: null,
    result_type: "needs_support",
    recommended_skus: [],
    install_on: null,
    message: "Proprietary brand fittings require brand-specific adapters.",
    install_detail: "Kärcher, Bosch AQT, Nilfisk, Lavor, and some Sun Joe systems use non-standard connections. Contact us with your brand and model number and we'll find the right solution.",
    warnings: ["Kärcher K-series connections are not M22 or QC compatible without a specific bridge adapter", "Do not assume proprietary fittings are compatible with universal parts"],
    confidence: "medium"
  },

  // =========================================================================
  // CONTEXT: hose_to_gun
  // =========================================================================

  {
    id: "hose_m22_14f_gun_m22_14m",
    context: "hose_to_gun",
    have_fitting: "m22_14_female",
    want_fitting: "m22_14_male",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "M22-14mm female hose connects directly to M22-14mm male gun inlet. No adapter needed.",
    install_detail: "Thread the hose end onto the gun inlet by hand, then snug with a wrench. No Teflon tape — M22 seals with an O-ring.",
    warnings: ["If it leaks, the O-ring may be worn — replace before assuming wrong size"],
    confidence: "high"
  },

  {
    id: "hose_m22_15f_gun_m22_15m",
    context: "hose_to_gun",
    have_fitting: "m22_15_female",
    want_fitting: "m22_15_male",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "M22-15mm female hose connects directly to M22-15mm male gun. No adapter needed.",
    install_detail: "Thread hose onto gun. No Teflon tape — M22 uses an O-ring.",
    warnings: ["Common Sun Joe / AR Blue system — stays within the 15mm ecosystem"],
    confidence: "high"
  },

  {
    id: "hose_qc38c_gun_qc38p",
    context: "hose_to_gun",
    have_fitting: "qc_3_8_coupler",
    want_fitting: "qc_3_8_plug",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your 3/8\" QC coupler snaps directly onto the gun's 3/8\" QC plug. No adapter needed.",
    install_detail: "Pull back the coupler sleeve, push onto the plug, release. It should click into place.",
    warnings: ["If it leaks at the snap joint, inspect and replace the QC O-ring"],
    confidence: "high"
  },

  {
    id: "hose_npt38m_gun_npt38f",
    context: "hose_to_gun",
    have_fitting: "npt_3_8_male",
    want_fitting: "npt_3_8_female",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "3/8\" NPT male hose end connects directly to 3/8\" NPT female gun inlet.",
    install_detail: "Apply Teflon tape to the hose end male threads. Thread into the gun inlet and tighten.",
    warnings: ["Always use thread sealant on NPT — it does not use O-rings"],
    confidence: "high"
  },

  {
    id: "hose_m22_14f_gun_qc38plug",
    context: "hose_to_gun",
    have_fitting: "m22_14_female",
    want_fitting: "qc_3_8_plug",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA25"],
    install_on: "hose",
    message: "You need one adapter to connect your M22 hose to the QC spray gun.",
    install_detail: "Thread SGY-PWA25 (M22 Male x 3/8\" QC Coupler) onto the hose's M22 female end. Then snap the gun's QC plug into the coupler.",
    warnings: ["No Teflon tape on the M22 end — uses O-ring"],
    confidence: "high"
  },

  {
    id: "hose_m22_14f_gun_npt38f",
    context: "hose_to_gun",
    have_fitting: "m22_14_female",
    want_fitting: "npt_3_8_female",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA23"],
    install_on: "hose",
    message: "You need one adapter: 3/8\" Male NPT × M22 Male.",
    install_detail: "Thread SGY-PWA23 (3/8\" Male NPT × M22 Male) into the hose's M22 female end (no tape — M22 uses an O-ring). Then apply thread sealant to the NPT end and thread it into the gun's 3/8\" female NPT inlet.",
    warnings: ["Use Teflon tape or pipe sealant on the NPT side only — not the M22 side"],
    confidence: "high"
  },

  {
    id: "hose_npt38m_gun_qc38plug",
    context: "hose_to_gun",
    have_fitting: "npt_3_8_male",
    want_fitting: "qc_3_8_plug",
    result_type: "multiple_products",
    recommended_skus: ["SGY-PWA24", "SGY-PWA25"],
    install_on: "hose",
    message: "You need two adapters to connect your 3/8\" NPT hose to a QC-equipped gun.",
    install_detail: "Recommended path: Hose (3/8\" NPT male) → SGY-PWA24 (Female 3/8\" NPT × Female M22/14mm) → SGY-PWA25 (Male M22/14mm × 3/8\" QC Coupler) → Gun inlet (3/8\" QC plug). Step 1 — Thread SGY-PWA24 onto the hose's 3/8\" NPT male end using thread sealant on the NPT threads. Step 2 — Thread SGY-PWA25 (M22 end) into SGY-PWA24's M22 female end — no tape needed, M22 seals with an O-ring. The 3/8\" QC coupler on SGY-PWA25 will now accept the gun's 3/8\" QC plug. Alternative: The 3/8\" NPT Female × 3/8\" QC Coupler piece included in the SGY-PWA6 kit can replace the two-adapter chain if you already own that kit.",
    warnings: ["Use thread sealant on the NPT connection only — not on M22 threads", "Confirm NPT threads are fully engaged before pressurizing", "M22 connections seal with an O-ring — check O-ring is present and undamaged"],
    confidence: "high"
  },

  {
    id: "hose_m22_14f_gun_m22_15m",
    context: "hose_to_gun",
    have_fitting: "m22_14_female",
    want_fitting: "m22_15_male",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA93"],
    install_on: "hose",
    message: "You need one adapter — SGY-PWA93 bridges M22-14mm hose to M22-15mm gun.",
    install_detail: "Thread SGY-PWA93's M22-14mm male end into the hose's M22-14mm female end (O-ring seal, no tape). The gun's M22-15mm male inlet then threads into SGY-PWA93's M22-15mm female end (O-ring seal, no tape). SGY-PWA93 is designed for Sun Joe SPX, AR Blue Clean, and Portland washers.",
    warnings: ["Do NOT thread a 14mm hose directly onto a 15mm gun without this adapter — it will leak under pressure", "No Teflon tape on either M22 end — both seal with O-rings"],
    confidence: "high"
  },

  {
    id: "hose_m22_15f_gun_m22_14m",
    context: "hose_to_gun",
    have_fitting: "m22_15_female",
    want_fitting: "m22_14_male",
    result_type: "possible_match",
    recommended_skus: [],
    install_on: "hose",
    message: "M22-15mm hose and M22-14mm gun don't connect directly — a third-party conversion coupler bridges the gap.",
    install_detail: "You need a M22-15mm Male to M22-14mm Female conversion coupler (not a SurfaceMaxx product). Thread the coupler's 15mm male end into the hose's M22-15mm female end, then the gun's M22-14mm male inlet threads into the coupler's 14mm female side. Available options: (1) Walmart: 1-Piece M22-15mm Male to 14mm Female Pressure Washer Coupler (https://www.walmart.com/ip/19996166027) — (2) Amazon: Boltigen M22-15mm to M22-14mm Connector (https://www.amazon.com/dp/B0CRPBSX87). Verify the coupler is rated for your pressure washer's PSI before use.",
    warnings: ["M22-14mm and M22-15mm look nearly identical — do not force a direct connection", "This coupler is not a SurfaceMaxx product — verify PSI rating"],
    confidence: "medium"
  },

  {
    id: "hose_ght_gun_any",
    context: "hose_to_gun",
    have_fitting: "ght_3_4_male",
    want_fitting: null,
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "A garden hose cannot connect to a spray gun on the high-pressure side.",
    install_detail: "Garden hose thread is low-pressure only. The spray gun is part of the high-pressure circuit. They are completely different systems.",
    warnings: ["SAFETY: Attempting this connection is dangerous — garden hose cannot handle pressure washer pressure"],
    confidence: "high"
  },

  {
    id: "hose_qc14_gun_qc38",
    context: "hose_to_gun",
    have_fitting: "qc_1_4_plug",
    want_fitting: "qc_3_8_coupler",
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "1/4\" QC and 3/8\" QC are not interchangeable.",
    install_detail: "1/4\" QC is the post-gun size (gun outlet to wand). 3/8\" QC is the pre-gun size (hose to gun). They are different sizes and will not snap together.",
    warnings: ["Using the wrong QC size is a common mistake — always match the QC size on both sides"],
    confidence: "high"
  },

  // =========================================================================
  // CONTEXT: gun_to_wand
  // =========================================================================

  {
    id: "gun_qc14c_wand_qc14p",
    context: "gun_to_wand",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "qc_1_4_plug",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your gun's 1/4\" QC coupler connects directly to the wand's 1/4\" QC plug. No adapter needed.",
    install_detail: "Pull back the coupler sleeve, push the wand plug in, release. Standard post-gun quick-connect.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "gun_m22_14f_wand_m22_14m",
    context: "gun_to_wand",
    have_fitting: "m22_14_female",
    want_fitting: "m22_14_male",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "M22-14mm female gun outlet connects directly to M22-14mm male wand. No adapter needed.",
    install_detail: "Thread the wand into the gun outlet. No Teflon tape — M22 uses an O-ring.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "gun_qc14c_wand_npt14m",
    context: "gun_to_wand",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "npt_1_4_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA304"],
    install_on: "wand",
    message: "You need one adapter to connect your QC gun to the threaded wand.",
    install_detail: "Thread SMX-PWA304 (1/4\" Female NPT x 1/4\" QC Plug) onto the wand's male NPT end with thread sealant. Then snap into the gun's QC coupler.",
    warnings: ["Use thread sealant on the NPT end of the adapter"],
    confidence: "high"
  },

  {
    id: "gun_qc14c_wand_m22_14m",
    context: "gun_to_wand",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "m22_14_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA315"],
    install_on: "wand",
    message: "You need one adapter: M22 Female x 1/4\" QC Plug.",
    install_detail: "Thread SMX-PWA315 (M22 Female x 1/4\" QC Plug) onto the wand's M22 male end. Then snap it into the gun's 1/4\" QC coupler.",
    warnings: ["No Teflon tape on M22 end — uses O-ring"],
    confidence: "high"
  },

  {
    id: "gun_npt14f_wand_npt14m",
    context: "gun_to_wand",
    have_fitting: "npt_1_4_female",
    want_fitting: "npt_1_4_male",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "1/4\" NPT female gun outlet connects directly to 1/4\" NPT male wand.",
    install_detail: "Apply thread sealant or Teflon tape to the wand's male NPT end. Thread into the gun outlet.",
    warnings: ["Use thread sealant — NPT does not use O-rings"],
    confidence: "high"
  },

  {
    id: "gun_qc14c_wand_qc38p",
    context: "gun_to_wand",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "qc_3_8_plug",
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "1/4\" QC and 3/8\" QC are not interchangeable — but you may be able to swap the fitting on your wand.",
    install_detail: "The gun outlet uses 1/4\" QC (post-gun standard). A 3/8\" plug will not snap into a 1/4\" coupler. However, if the 3/8\" QC plug on the wand inlet is removable (not crimped or molded on), you may be able to unscrew it and replace it with a 1/4\" QC plug to match the gun's 1/4\" coupler. Check whether the plug on your wand unscrews — if so, a standard 1/4\" NPT × 1/4\" QC plug replacement will restore compatibility.",
    warnings: ["3/8\" QC is the pre-gun hose size — not used post-gun on standard systems", "Do not force a 3/8\" plug into a 1/4\" coupler", "If the wand fitting is crimped or molded on, it cannot be swapped — contact us for support"],
    confidence: "high"
  },

  {
    id: "gun_qc14c_wand_ght",
    context: "gun_to_wand",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "ght_3_4_male",
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "A garden hose attachment cannot connect to the gun outlet.",
    install_detail: "Garden hose thread is not compatible with pressure washer QC or threaded wand fittings.",
    warnings: [],
    confidence: "high"
  },

  // =========================================================================
  // CONTEXT: wand_to_attachment
  // =========================================================================

  {
    id: "wand_qc14c_attach_qc14p",
    context: "wand_to_attachment",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "qc_1_4_plug",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your wand's 1/4\" QC coupler connects directly to the attachment's 1/4\" QC plug. No adapter needed.",
    install_detail: "Pull back the wand's coupler sleeve, push the attachment plug in, release. Standard post-gun connection.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "wand_qc14c_attach_npt14m",
    context: "wand_to_attachment",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "npt_1_4_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA317"],
    install_on: "attachment",
    message: "You need one adapter: 1/4\" Male NPT x 1/4\" QC Plug.",
    install_detail: "Apply thread sealant to SMX-PWA317 (1/4\" Male NPT x 1/4\" QC Plug) and thread it into the attachment's female NPT port. Then snap into the wand's QC coupler.",
    warnings: ["Use Teflon tape on the NPT end"],
    confidence: "high"
  },

  {
    id: "wand_qc14c_attach_m22_14m",
    context: "wand_to_attachment",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "m22_14_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA315"],
    install_on: "attachment",
    message: "You need one adapter: M22 Female x 1/4\" QC Plug.",
    install_detail: "Thread SMX-PWA315 onto the attachment's M22 male end. Snap into the wand's QC coupler.",
    warnings: ["No Teflon tape on M22 — uses O-ring"],
    confidence: "high"
  },

  {
    id: "wand_m22_14m_attach_qc14p",
    context: "wand_to_attachment",
    have_fitting: "m22_14_male",
    want_fitting: "qc_1_4_plug",
    result_type: "multiple_products",
    recommended_skus: ["SMX-PWA309", "SMX-PWA316"],
    install_on: "wand",
    message: "This combination is uncommon — you'll need two adapters to bridge M22 to 1/4\" QC.",
    install_detail: "This is a rare combination (M22 wand outlet to 1/4\" QC attachment). You'll need two pieces: Step 1 — Thread SMX-PWA309 (Female M22/14mm × Female 1/4\" NPT) onto the wand's M22-14mm male outlet — no tape needed, M22 seals with an O-ring. Step 2 — Thread SMX-PWA316 (Male 1/4\" NPT × 1/4\" QC Coupler) into SMX-PWA309's 1/4\" NPT female end using thread sealant. The 1/4\" QC coupler will now accept your attachment's 1/4\" QC plug.",
    warnings: ["This is an unusual combination — double-check your wand's outlet is truly M22 (not 1/4\" QC already)", "Use thread sealant on the 1/4\" NPT connection between SMX-PWA309 and SMX-PWA316", "M22 end seals with O-ring — no tape needed there"],
    confidence: "medium"
  },

  {
    id: "wand_qc14c_attach_ght",
    context: "wand_to_attachment",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "ght_3_4_male",
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "Garden hose thread attachments cannot connect to the pressure washer wand.",
    install_detail: "GHT is for low-pressure garden use only. The wand operates at high pressure and uses 1/4\" QC or threaded NPT/M22 fittings.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "wand_qc14c_attach_qc38p",
    context: "wand_to_attachment",
    have_fitting: "qc_1_4_coupler",
    want_fitting: "qc_3_8_plug",
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "A 3/8\" QC plug is too large for the wand's 1/4\" QC coupler.",
    install_detail: "3/8\" QC is the pre-gun hose size. Post-wand accessories all use 1/4\" QC. These two sizes do not interchange.",
    warnings: ["Double-check your accessory — most standard attachments (nozzles, foam cannons, surface cleaners) are 1/4\" QC"],
    confidence: "high"
  },

  // =========================================================================
  // CONTEXT: hose_to_hose
  // =========================================================================

  {
    id: "hose_m22_14f_hose2_m22_14m",
    context: "hose_to_hose",
    have_fitting: "m22_14_female",
    want_fitting: "m22_14_male",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "M22-14mm female to M22-14mm male — thread directly together.",
    install_detail: "Thread the female end onto the male end. No Teflon tape — M22 uses an O-ring.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "hose_m22_14f_hose2_m22_14f",
    context: "hose_to_hose",
    have_fitting: "m22_14_female",
    want_fitting: "m22_14_female",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA7"],
    install_on: "between_hoses",
    message: "You need a male-male coupler to join two M22 female hose ends.",
    install_detail: "Thread SGY-PWA7 (M22 Male x M22 Male Hose Connector) between the two hose ends. This connects two M22 female ends together for hose extension.",
    warnings: ["Adding hose length reduces pressure slightly — stay within the pressure washer's rated hose length"],
    confidence: "high"
  },

  {
    id: "hose_qc38c_hose2_qc38p",
    context: "hose_to_hose",
    have_fitting: "qc_3_8_coupler",
    want_fitting: "qc_3_8_plug",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "3/8\" QC coupler to 3/8\" QC plug — snap directly together.",
    install_detail: "Pull back the coupler sleeve, push the plug in, release.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "hose_ght_hose2_hp",
    context: "hose_to_hose",
    have_fitting: "ght_3_4_male",
    want_fitting: "m22_14_female",
    result_type: "not_compatible",
    recommended_skus: [],
    install_on: null,
    message: "A garden hose CANNOT connect to the high-pressure hose circuit.",
    install_detail: "Garden hose (GHT) is low-pressure water supply only. High-pressure hoses use M22, QC, or NPT fittings rated for pressure washer discharge. Do not connect these.",
    warnings: ["SAFETY: Garden hose is not rated for pressure washer discharge pressure"],
    confidence: "high"
  },

  // =========================================================================
  // UNKNOWN FITTING BRANCHES
  // =========================================================================

  {
    id: "unknown_have_pump_to_hose",
    context: "pump_to_hose",
    have_fitting: "unknown",
    want_fitting: null,
    result_type: "possible_match",
    recommended_skus: ["SGY-PWA10", "SGY-PWA26", "SGY-PWA27"],
    install_on: "pump_or_hose",
    message: "Based on most gas pressure washers, the most likely fitting is M22-14mm. Here are the most common adapters for pump-to-hose connections.",
    install_detail: "To confirm: look at your pump outlet. If it has threads you can see (not a snap/click fitting), count the outer thread diameter — it should be marked M22 or be approximately 22mm across.",
    warnings: ["Sun Joe, AR Blue Clean, and Portland washers often use M22-15mm instead — check your manual"],
    confidence: "low"
  },

  {
    id: "unknown_want_gun_to_wand",
    context: "gun_to_wand",
    have_fitting: null,
    want_fitting: "unknown",
    result_type: "possible_match",
    recommended_skus: ["SGY-PWA3"],
    install_on: "gun_or_wand",
    message: "Most modern spray guns have a 1/4\" QC coupler on the outlet. Here are the most common wand connection adapters.",
    install_detail: "To confirm: look at the front of your gun where the wand attaches. If it has a spring-loaded collar (you pull it back to insert/remove the wand), it is a 1/4\" QC coupler.",
    warnings: ["Some older or commercial guns have threaded outlets — measure or check your manual"],
    confidence: "low"
  }
,
  // =========================================================================
  // GAP RULES — pump_to_hose
  // Added from gap analysis (Apr 20 2026 follow-up)
  // =========================================================================

  {
    id: "gap_pump_npt38m_hose_m22_14f",
    context: "pump_to_hose",
    have_fitting: "npt_3_8_male",
    want_fitting: "m22_14_female",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA313"],
    install_on: "pump",
    message: "You need one adapter: SMX-PWA313 (M22-14mm Male × 3/8\" Female NPT).",
    install_detail: "Thread SMX-PWA313's 3/8\" female NPT end onto the pump's 3/8\" NPT male outlet using thread sealant. The hose's M22-14mm female end threads directly onto SMX-PWA313's M22-14mm male end — no tape, O-ring seal.",
    warnings: ["Use thread sealant on the NPT end only — not on M22 threads"],
    confidence: "high"
  },

  {
    id: "gap_pump_npt38f_hose_m22_14f",
    context: "pump_to_hose",
    have_fitting: "npt_3_8_female",
    want_fitting: "m22_14_female",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA23"],
    install_on: "between",
    message: "You need one adapter: SGY-PWA23 (M22-14mm Male × 3/8\" NPT Male) to bridge two female ends.",
    install_detail: "SGY-PWA23 has male ends on both sides. Thread the 3/8\" NPT male end into the pump's 3/8\" NPT female outlet using thread sealant. Thread the M22-14mm male end into the hose's M22-14mm female end — O-ring seal, no tape.",
    warnings: ["Use thread sealant on the NPT end only"],
    confidence: "high"
  },

  {
    id: "gap_pump_npt38f_hose_qc38coupler",
    context: "pump_to_hose",
    have_fitting: "npt_3_8_female",
    want_fitting: "qc_3_8_coupler",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA22"],
    install_on: "pump",
    message: "You need one adapter: SGY-PWA22 (3/8\" NPT Male × 3/8\" QC Plug).",
    install_detail: "Thread SGY-PWA22's 3/8\" NPT male end into the pump's 3/8\" NPT female outlet using thread sealant. SGY-PWA22's 3/8\" QC plug end then snaps into the hose's 3/8\" QC coupler.",
    warnings: ["Use thread sealant on the NPT connection"],
    confidence: "high"
  },

  {
    id: "gap_pump_m22_14m_hose_qc38plug",
    context: "pump_to_hose",
    have_fitting: "m22_14_male",
    want_fitting: "qc_3_8_plug",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA30"],
    install_on: "pump",
    message: "You need one adapter: SGY-PWA30 (M22-14mm Female × 3/8\" QC Coupler).",
    install_detail: "Thread SGY-PWA30 onto the pump's M22-14mm male outlet — O-ring seal, no tape. SGY-PWA30's 3/8\" QC coupler will then accept the hose's 3/8\" QC plug.",
    warnings: ["No Teflon tape on M22 — O-ring seal only"],
    confidence: "high"
  },

  // =========================================================================
  // GAP RULES — hose_to_gun
  // =========================================================================

  {
    id: "gap_hose_m22_15f_gun_qc38plug",
    context: "hose_to_gun",
    have_fitting: "m22_15_female",
    want_fitting: "qc_3_8_plug",
    result_type: "multiple_products",
    recommended_skus: ["SGY-PWA25"],
    install_on: "hose",
    message: "You need a third-party M22-15mm→14mm coupler plus SGY-PWA25 to complete this connection.",
    install_detail: "Step 1 — Thread a third-party M22-15mm Male to M22-14mm Female conversion coupler onto the hose's M22-15mm female end (Walmart: https://www.walmart.com/ip/19996166027). Step 2 — Thread SGY-PWA25's M22-14mm male end into that coupler's M22-14mm female side (O-ring, no tape). SGY-PWA25's 3/8\" QC coupler then accepts the gun's 3/8\" QC plug.",
    warnings: ["Verify the third-party coupler is rated for your washer's PSI", "No tape on M22 ends — O-ring seal"],
    confidence: "medium"
  },

  {
    id: "gap_hose_m22_14f_gun_m22_14f",
    context: "hose_to_gun",
    have_fitting: "m22_14_female",
    want_fitting: "m22_14_female",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA7"],
    install_on: "between",
    message: "Both ends are M22-14mm female — you need SGY-PWA7 (M22-14mm Male × M22-14mm Male) to join them.",
    install_detail: "Thread SGY-PWA7's M22-14mm male ends into each M22-14mm female opening — O-ring seal on both ends, no tape.",
    warnings: ["No Teflon tape on either M22 end"],
    confidence: "high"
  },

  {
    id: "gap_hose_qc38coupler_gun_npt38f",
    context: "hose_to_gun",
    have_fitting: "qc_3_8_coupler",
    want_fitting: "npt_3_8_female",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA22"],
    install_on: "gun",
    message: "You need one adapter: SGY-PWA22 (3/8\" NPT Male × 3/8\" QC Plug).",
    install_detail: "Thread SGY-PWA22's 3/8\" NPT male end into the gun's 3/8\" NPT female inlet using thread sealant. SGY-PWA22's 3/8\" QC plug end snaps into the hose's 3/8\" QC coupler.",
    warnings: ["Use thread sealant on the NPT connection"],
    confidence: "high"
  },

  {
    id: "gap_hose_npt38m_gun_m22_14m",
    context: "hose_to_gun",
    have_fitting: "npt_3_8_male",
    want_fitting: "m22_14_male",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA24"],
    install_on: "between",
    message: "Both ends are male — SGY-PWA24 (M22-14mm Female × 3/8\" NPT Female) bridges the two male fittings.",
    install_detail: "Thread SGY-PWA24's 3/8\" NPT female end onto the hose's 3/8\" NPT male end using thread sealant. Thread the gun's M22-14mm male inlet into SGY-PWA24's M22-14mm female end — O-ring seal, no tape.",
    warnings: ["Use thread sealant on the NPT end only"],
    confidence: "high"
  },

  // =========================================================================
  // GAP RULES — gun_to_wand (GAP-9 through GAP-12f)
  // =========================================================================

  {
    id: "gap_gun_m22_14f_wand_qc14plug",
    context: "gun_to_wand",
    have_fitting: "m22_14_female",
    want_fitting: "qc_1_4_plug",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA98"],
    install_on: "gun",
    message: "The coupler piece from SGY-PWA98 connects your M22-14mm gun outlet to the wand's 1/4\" QC plug.",
    install_detail: "From the SGY-PWA98 kit, use the M22-14mm Male × 1/4\" QC Coupler piece. Thread its M22-14mm male end into the gun's M22-14mm female outlet (O-ring seal, no tape). The 1/4\" QC coupler end then accepts the wand's 1/4\" QC plug. Note: this piece is only available as part of the SGY-PWA98 set — no standalone SKU.",
    warnings: ["Purchase SGY-PWA98 and use the coupler piece — the plug piece will become a spare", "No tape on M22 — O-ring seal only"],
    confidence: "high"
  },

  {
    id: "gap_gun_m22_14f_wand_npt14m",
    context: "gun_to_wand",
    have_fitting: "m22_14_female",
    want_fitting: "npt_1_4_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA312"],
    install_on: "gun",
    message: "You need one adapter: SMX-PWA312 (M22-14mm Male × 1/4\" NPT Female).",
    install_detail: "Thread SMX-PWA312's M22-14mm male end into the gun's M22-14mm female outlet (O-ring, no tape). The wand's 1/4\" NPT male end threads into SMX-PWA312's 1/4\" NPT female end using thread sealant.",
    warnings: ["Use thread sealant on the 1/4\" NPT connection"],
    confidence: "high"
  },

  {
    id: "gap_gun_npt38f_wand_qc14plug",
    context: "gun_to_wand",
    have_fitting: "npt_3_8_female",
    want_fitting: "qc_1_4_plug",
    result_type: "multiple_products",
    recommended_skus: ["SMX-PWA311", "SGY-PWA98"],
    install_on: "gun",
    message: "You need two products to bridge a 3/8\" NPT gun outlet to a 1/4\" QC wand.",
    install_detail: "Step 1 — Thread SMX-PWA311 (M22-14mm Female × 3/8\" NPT Male) into the gun's 3/8\" NPT female outlet using thread sealant. Step 2 — From the SGY-PWA98 kit, use the M22-14mm Male × 1/4\" QC Coupler piece: thread its M22-14mm male end into SMX-PWA311's M22-14mm female end (O-ring, no tape). The 1/4\" QC coupler then accepts the wand's 1/4\" QC plug.",
    warnings: ["Use thread sealant on the NPT connection", "SGY-PWA98 is a kit — no standalone SKU for the coupler piece"],
    confidence: "medium"
  },

  {
    id: "gap_gun_m22_14m_wand_qc14plug",
    context: "gun_to_wand",
    have_fitting: "m22_14_male",
    want_fitting: "qc_1_4_plug",
    result_type: "multiple_products",
    recommended_skus: ["SMX-PWA310", "SMX-PWA300"],
    install_on: "gun",
    message: "You need two adapters to connect your M22-14mm male gun outlet to a 1/4\" QC wand.",
    install_detail: "Step 1 — Thread SMX-PWA310 (M22-14mm Female × 1/4\" NPT Male) onto the gun's M22-14mm male outlet (O-ring, no tape). Step 2 — Thread SMX-PWA300 (1/4\" NPT Female × 1/4\" QC Coupler) onto SMX-PWA310's 1/4\" NPT male end using thread sealant. The 1/4\" QC coupler accepts the wand's 1/4\" QC plug.",
    warnings: ["Use thread sealant on the 1/4\" NPT connection between SMX-PWA310 and SMX-PWA300"],
    confidence: "high"
  },

  {
    id: "gap_gun_m22_14m_wand_npt14m",
    context: "gun_to_wand",
    have_fitting: "m22_14_male",
    want_fitting: "npt_1_4_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA309"],
    install_on: "gun",
    message: "You need one adapter: SMX-PWA309 (M22-14mm Female × 1/4\" NPT Female) to bridge two male ends.",
    install_detail: "Thread SMX-PWA309's M22-14mm female end onto the gun's M22-14mm male outlet (O-ring, no tape). Thread the wand's 1/4\" NPT male end into SMX-PWA309's 1/4\" NPT female end using thread sealant.",
    warnings: ["Use thread sealant on the 1/4\" NPT connection"],
    confidence: "high"
  },

  {
    id: "gap_gun_m22_14m_wand_npt38m",
    context: "gun_to_wand",
    have_fitting: "m22_14_male",
    want_fitting: "npt_3_8_male",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA24"],
    install_on: "gun",
    message: "You need one adapter: SGY-PWA24 (M22-14mm Female × 3/8\" NPT Female) to bridge two male ends.",
    install_detail: "Thread SGY-PWA24's M22-14mm female end onto the gun's M22-14mm male outlet (O-ring, no tape). Thread the wand's 3/8\" NPT male end into SGY-PWA24's 3/8\" NPT female end using thread sealant.",
    warnings: ["Use thread sealant on the 3/8\" NPT connection"],
    confidence: "high"
  },

  {
    id: "gap_gun_m22_14m_wand_qc14coupler",
    context: "gun_to_wand",
    have_fitting: "m22_14_male",
    want_fitting: "qc_1_4_coupler",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA315"],
    install_on: "gun",
    message: "You need one adapter: SMX-PWA315 (M22-14mm Female × 1/4\" QC Plug).",
    install_detail: "Thread SMX-PWA315's M22-14mm female end onto the gun's M22-14mm male outlet (O-ring, no tape). SMX-PWA315's 1/4\" QC plug end snaps into the wand's 1/4\" QC coupler inlet.",
    warnings: ["No tape on M22 — O-ring seal only"],
    confidence: "high"
  },

  {
    id: "gap_gun_m22_14m_wand_m22_14m",
    context: "gun_to_wand",
    have_fitting: "m22_14_male",
    want_fitting: "m22_14_male",
    result_type: "multiple_products",
    recommended_skus: ["SMX-PWA309", "SMX-PWA310"],
    install_on: "gun",
    message: "Both gun outlet and wand inlet are M22-14mm male — two adapters bridge them via a 1/4\" NPT junction.",
    install_detail: "Step 1 — Thread SMX-PWA309 (M22-14mm Female × 1/4\" NPT Female) onto the gun's M22-14mm male outlet (O-ring, no tape). Step 2 — Thread SMX-PWA310 (M22-14mm Female × 1/4\" NPT Male) into SMX-PWA309's 1/4\" NPT female end using thread sealant. SMX-PWA310's M22-14mm female end then accepts the wand's M22-14mm male inlet (O-ring, no tape).",
    warnings: ["Use thread sealant on the 1/4\" NPT junction between the two adapters only"],
    confidence: "high"
  },

  {
    id: "gap_gun_m22_14m_wand_m22_15m",
    context: "gun_to_wand",
    have_fitting: "m22_14_male",
    want_fitting: "m22_15_male",
    result_type: "multiple_products",
    recommended_skus: ["SMX-PWA309", "SMX-PWA310", "SGY-PWA93"],
    install_on: "gun",
    message: "M22 bore mismatch plus both ends male — three adapters chain together to make this connection.",
    install_detail: "Step 1 — Thread SMX-PWA309 (M22-14mm Female × 1/4\" NPT Female) onto the gun's M22-14mm male outlet (O-ring, no tape). Step 2 — Thread SMX-PWA310 (M22-14mm Female × 1/4\" NPT Male) into SMX-PWA309's 1/4\" NPT female end using thread sealant. Step 3 — Thread SGY-PWA93 (M22-15mm Female × M22-14mm Male) into SMX-PWA310's M22-14mm female end (O-ring, no tape). The wand's M22-15mm male inlet then threads into SGY-PWA93's M22-15mm female end (O-ring, no tape).",
    warnings: ["Thread sealant on the 1/4\" NPT junction only — all M22 connections use O-rings, no tape", "This is an unusual combination — double-check all fitting sizes before purchasing"],
    confidence: "medium"
  },

  // =========================================================================
  // GAP RULES — wand_to_attachment (GAP-13, 14, 15)
  // =========================================================================

  {
    id: "gap_wand_m22_14m_attach_m22_14f",
    context: "wand_to_attachment",
    have_fitting: "m22_14_male",
    want_fitting: "m22_14_female",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "Your M22-14mm male wand outlet connects directly to the M22-14mm female attachment inlet. No adapter needed.",
    install_detail: "Thread the wand's M22-14mm male tip into the attachment's M22-14mm female inlet. O-ring seal — no Teflon tape needed.",
    warnings: [],
    confidence: "high"
  },

  {
    id: "gap_wand_m22_14m_attach_npt14m",
    context: "wand_to_attachment",
    have_fitting: "m22_14_male",
    want_fitting: "npt_1_4_male",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA309"],
    install_on: "wand",
    message: "You need one adapter: SMX-PWA309 (M22-14mm Female × 1/4\" NPT Female) to bridge two male ends.",
    install_detail: "Thread SMX-PWA309's M22-14mm female end onto the wand's M22-14mm male tip (O-ring, no tape). Thread the attachment's 1/4\" NPT male end into SMX-PWA309's 1/4\" NPT female end using thread sealant.",
    warnings: ["Use thread sealant on the 1/4\" NPT connection"],
    confidence: "high"
  },

  {
    id: "gap_wand_npt14m_attach_qc14plug",
    context: "wand_to_attachment",
    have_fitting: "npt_1_4_male",
    want_fitting: "qc_1_4_plug",
    result_type: "adapter_needed",
    recommended_skus: ["SMX-PWA300"],
    install_on: "wand",
    message: "You need one adapter: SMX-PWA300 (1/4\" NPT Female × 1/4\" QC Coupler).",
    install_detail: "Thread SMX-PWA300's 1/4\" NPT female end onto the wand's 1/4\" NPT male tip using thread sealant. SMX-PWA300's 1/4\" QC coupler then accepts the attachment's 1/4\" QC plug.",
    warnings: ["Use thread sealant on the NPT connection"],
    confidence: "high"
  },

  // =========================================================================
  // GAP RULES — hose_to_hose (GAP-16, 17, 18, 20, 21)
  // =========================================================================

  {
    id: "gap_hose_npt38m_hose2_npt38f",
    context: "hose_to_hose",
    have_fitting: "npt_3_8_male",
    want_fitting: "npt_3_8_female",
    result_type: "direct_fit",
    recommended_skus: [],
    install_on: null,
    message: "3/8\" NPT male to 3/8\" NPT female — thread directly together with sealant. No adapter needed.",
    install_detail: "Apply thread sealant or Teflon tape to the male NPT end. Thread the two hoses together.",
    warnings: ["Always use thread sealant — NPT connections do not use O-rings"],
    confidence: "high"
  },

  {
    id: "gap_hose_qc38coupler_hose2_m22_14m",
    context: "hose_to_hose",
    have_fitting: "qc_3_8_coupler",
    want_fitting: "m22_14_male",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA27"],
    install_on: "hose2",
    message: "You need one adapter: SGY-PWA27 (M22-14mm Female × 3/8\" QC Plug).",
    install_detail: "Thread SGY-PWA27's M22-14mm female end onto the second hose's M22-14mm male end (O-ring, no tape). SGY-PWA27's 3/8\" QC plug snaps into the first hose's 3/8\" QC coupler.",
    warnings: ["No tape on M22 — O-ring seal only"],
    confidence: "high"
  },

  {
    id: "gap_hose_m22_14f_hose2_qc38plug",
    context: "hose_to_hose",
    have_fitting: "m22_14_female",
    want_fitting: "qc_3_8_plug",
    result_type: "adapter_needed",
    recommended_skus: ["SGY-PWA25"],
    install_on: "hose1",
    message: "You need one adapter: SGY-PWA25 (M22-14mm Male × 3/8\" QC Coupler).",
    install_detail: "Thread SGY-PWA25's M22-14mm male end into the first hose's M22-14mm female end (O-ring, no tape). SGY-PWA25's 3/8\" QC coupler accepts the second hose's 3/8\" QC plug.",
    warnings: ["No tape on M22 — O-ring seal only"],
    confidence: "high"
  },

  {
    id: "gap_hose_m22_14f_hose2_m22_15f",
    context: "hose_to_hose",
    have_fitting: "m22_14_female",
    want_fitting: "m22_15_female",
    result_type: "possible_match",
    recommended_skus: [],
    install_on: null,
    message: "M22-14mm and M22-15mm hoses won't connect directly — a third-party twist union bridges the bore difference.",
    install_detail: "A ProTool M22-14mm to M22-15mm Twist Union Brass adapter connects these two bore sizes. Available at jracenstein.com: https://jracenstein.com/protool-m22-14mm-to-m22-15mm-twist-union-brass/?sku=74-4231. This is not a SurfaceMaxx product — verify PSI rating for your application.",
    warnings: ["M22-14mm and M22-15mm will leak if connected directly without this adapter", "Third-party product — verify PSI rating"],
    confidence: "medium"
  },

  {
    id: "gap_hose_m22_15f_hose2_m22_14m",
    context: "hose_to_hose",
    have_fitting: "m22_15_female",
    want_fitting: "m22_14_male",
    result_type: "possible_match",
    recommended_skus: [],
    install_on: null,
    message: "M22-15mm and M22-14mm hoses won't connect directly — a third-party conversion coupler bridges the bore difference.",
    install_detail: "A M22-15mm Male to M22-14mm Female conversion coupler connects these two sizes: Walmart: 1-Piece M22-15mm Male to 14mm Female Pressure Washer Coupler (https://www.walmart.com/ip/19996166027). Thread the coupler's 15mm male end into the first hose's 15mm female end, then the second hose's 14mm male end goes into the coupler's 14mm female side. Verify PSI rating before use.",
    warnings: ["Third-party product — verify PSI rating", "Do not force a direct M22-15mm to M22-14mm connection without this adapter"],
    confidence: "medium"
  },

  // =========================================================================
  // UNKNOWN FITTING FALLBACKS — hose_to_gun, wand_to_attachment, hose_to_hose
  // =========================================================================

  {
    id: "unknown_have_hose_to_gun",
    context: "hose_to_gun",
    have_fitting: "unknown",
    want_fitting: null,
    result_type: "possible_match",
    recommended_skus: ["SGY-PWA10", "SGY-PWA6"],
    install_on: "hose_or_gun",
    message: "Most hoses use M22-14mm female ends or 3/8\" QC couplers. Here are the most common hose-to-gun adapters.",
    install_detail: "To identify your hose end: if it has a large threaded collar (about the size of a quarter), it is likely M22 — measure the bore to confirm 14mm vs 15mm. If it has a spring-loaded push fitting, it is a 3/8\" QC coupler. If it has small tapered threads, it is 3/8\" NPT.",
    warnings: ["Confirm your hose end type before purchasing — M22-14mm and M22-15mm look identical but are not interchangeable"],
    confidence: "low"
  },

  {
    id: "unknown_have_wand_to_attachment",
    context: "wand_to_attachment",
    have_fitting: "unknown",
    want_fitting: null,
    result_type: "possible_match",
    recommended_skus: ["SGY-PWA3", "SGY-PWA98"],
    install_on: "wand_or_attachment",
    message: "Most wand tips use 1/4\" QC couplers. Here are the most common wand-to-attachment adapters.",
    install_detail: "To identify your wand tip: if it has a spring-loaded collar (pull back to insert accessories), it is a 1/4\" QC coupler — the most common post-gun standard. If it has small threads, measure the diameter — 1/4\" NPT is about 13mm OD.",
    warnings: ["3/8\" QC is the pre-gun (hose-to-gun) size and will not work post-gun — confirm you have 1/4\" QC"],
    confidence: "low"
  },

  {
    id: "unknown_have_hose_to_hose",
    context: "hose_to_hose",
    have_fitting: "unknown",
    want_fitting: null,
    result_type: "possible_match",
    recommended_skus: ["SGY-PWA7", "SGY-PWA10"],
    install_on: "between_hoses",
    message: "Most high-pressure hoses use M22-14mm female ends or 3/8\" QC fittings. Here are the most common hose-joiner options.",
    install_detail: "To identify your hose end: large threaded collar = likely M22. Spring-loaded push fitting = 3/8\" QC. Small tapered threads = 3/8\" NPT. SGY-PWA7 (M22 male × M22 male) joins two M22 female ends. For QC hose ends, match coupler to plug.",
    warnings: ["Adding hose length reduces pressure slightly — stay within your pressure washer's rated hose length"],
    confidence: "low"
  }


];